import React, { useEffect, useRef, useState } from "react";
import { Col, Row, Stack } from "react-bootstrap";
import Textarea from "../../../ui/Textarea";
import SelectFamily from "../../../ui/SelectFamily";
import SelectObject from "../../../ui/SelectObject";
import DatePicker from "../../../ui/DatePicker";
import { User } from "../../../../types/user";
import { Note } from "../../../../types/note";
import FormCheck from "../../../ui/FormCheckbox";
import Input from "../../../ui/Input";
import { fetchFieldChoices } from "../../../../services/httpClient";
import ViewRecordings from "./ViewRecordings";

interface CustomState {
  note?: Note;
  activeFamily: User[];
  familyUser: string;
  setFamilyUser: any;
  isBanned?: boolean;
  isDeleted?: boolean;
  isSelectedDisabled?: boolean;
  type?: any;
  setUpdatedNote: any;
  setSaveIsEnabled: any;
  setUserToSave: any;
  myCase?: boolean;
  editNote?: boolean;
}

const CustomNote = ({
  note,
  activeFamily,
  familyUser,
  setFamilyUser,
  isBanned = false,
  isDeleted = false,
  isSelectedDisabled = false,
  type,
  setUpdatedNote,
  setSaveIsEnabled,
  setUserToSave,
  myCase,
  editNote = false,
}: CustomState) => {
  const mainRef = useRef();
  const [noteDate, setNoteDate] = useState<string>("");
  const [fields, setFields] = useState<any[]>([]);
  const [fieldsWithOptions, setFieldsWithOptions] = useState<any[]>([]);

  const possibleHours: any[] = [
    { name: "0", key: "0" },
    { name: "1", key: "1" },
    { name: "2", key: "2" },
    { name: "3", key: "3" },
    { name: "4", key: "4" },
    { name: "5", key: "5" },
    { name: "6", key: "6" },
    { name: "7", key: "7" },
    { name: "8", key: "8" },
    { name: "9", key: "9" },
    { name: "10", key: "10" },
  ];
  const possibleMins: any[] = [
    { name: "0", key: "0" },
    { name: "5", key: "5" },
    { name: "10", key: "10" },
    { name: "15", key: "15" },
    { name: "20", key: "20" },
    { name: "25", key: "25" },
    { name: "30", key: "30" },
    { name: "35", key: "35" },
    { name: "40", key: "40" },
    { name: "45", key: "45" },
    { name: "50", key: "50" },
    { name: "55", key: "55" },
  ];

  const handleScroll = (ref: any) => {
    if (ref.current) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "end", inline: "nearest" });
    }
  };

  useEffect(() => {
    handleScroll(mainRef);

    if (editNote) {
      //remove date field from fields
      setFields(note?.fields ? note.fields.filter((f: any) => f.key !== "date") : []);

      const dateField = note?.fields?.find((field: any) => field.key === "date");
      setNoteDate(dateField?.value ?? null);
    } else {
      //remove date field from fields
      setFields(type?.fields ? type.fields.filter((f: any) => f.key !== "date") : []);
      setNoteDate(new Date().toISOString().split("T")[0]);
    }
  }, []);

  useEffect(() => {
    var upNote;
    const parts = familyUser.split("|");
    if (fieldsWithOptions.length > 0) {
      const fieldPairs = fieldsWithOptions.reduce((acc, field) => {
        acc[field.key] = field.value ?? null; // use field.value or default to null
        return acc;
      }, {});

      upNote = {
        type: "custom",
        notetype: editNote ? note?.notetype : type?.key,
        notetypename: editNote ? note?.notetypename : type?.name,
        subject:
          familyUser !== "unassigned"
            ? {
                id: parts[0],
                type: parts[1],
              }
            : { id: 0 },
        fields: fieldPairs,
        date: noteDate,
      };
    }

    setUpdatedNote(upNote);
  }, [noteDate, familyUser, fieldsWithOptions, setUpdatedNote, type?.key]);

  useEffect(() => {
    if (!fields || fields.length === 0) return;

    const fetchDropdowns = async () => {
      const updated = await Promise.all(
        fields.map(async (f: any) => {
          if (f.type !== "dropdown") return f;

          try {
            const res = await fetchFieldChoices(note?.notetype_id, f.id);
            const options = res[0]?.data;
            return { ...f, options }; // ✅ attach options to the field
          } catch (err) {
            console.error(err);
            return { ...f, options: [] };
          }
        }),
      );

      setFieldsWithOptions(updated); // store enriched array
    };

    fetchDropdowns();
  }, [fields, note?.notetype_id]);

  const updateFamilyUser = (e: any) => {
    setFamilyUser(e.target.value);

    if (noteDate) {
      setSaveIsEnabled(true);
      setUserToSave(true);
    }
  };

  const updateField = (e: any, fieldObj: any, index: number, part?: "hours" | "minutes") => {
    setFieldsWithOptions((prevFields) =>
      prevFields.map((field, i) => {
        if (i !== index) return field;

        // Account for single number storage
        if (fieldObj.type === "time" && part) {
          const current = Number(field.value) || 0;

          // get current hours/minutes
          const currentHours = Math.floor(current / 60);
          const currentMinutes = current % 60;

          // parse
          const incoming = Number(e.target.value) || 0;

          let newTotal = current;
          if (part === "hours") {
            newTotal = incoming * 60 + currentMinutes;
          } else {
            newTotal = currentHours * 60 + incoming;
          }

          return { ...field, value: String(newTotal) };
        }

        // Checkbox uses checked, others use value
        const newValue = fieldObj.type === "checkbox" ? e.target.checked : e.target.value;
        return { ...field, value: newValue };
      }),
    );

    if (noteDate) {
      setSaveIsEnabled(true);
      setUserToSave(true);
    }
  };

  const updateDate = () => {
    setSaveIsEnabled(true);
    setUserToSave(true);
  };

  useEffect(() => {
    setFieldsWithOptions((prevFields) =>
      prevFields.map((field, i) =>
        field.key === "date"
          ? { ...field, value: noteDate } // or whatever key you're updating
          : field,
      ),
    );

    if (!noteDate) {
      setSaveIsEnabled(false);
      setUserToSave(false);
    }
  }, [noteDate]);

  const getMaxDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getMinDate = () => {
    const today = new Date();
    const year = today.getFullYear() - 100;
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  return (
    <>
      <Row className="mt-4" ref={mainRef}>
        {isSelectedDisabled || !myCase ? (
          <Col xs={6}>
            <Input
              label="Household Member"
              type="text"
              value={familyUser.split("|")[2]}
              cssInput={isDeleted || isBanned ? "app-danger-text" : ""}
              disabled={true}
            />
          </Col>
        ) : (
          <SelectFamily
            family={activeFamily}
            familyUser={familyUser ? familyUser : "unassigned"}
            updateFamilyUser={updateFamilyUser}
            defaultSelection="Unassigned"
          />
        )}
        <Col className="col-lg-4 col-md-6 col-sm-8">
          <DatePicker
            label="Date"
            currentValue={noteDate}
            setCurrentValue={setNoteDate}
            onUpdate={() => updateDate()}
            required={true}
            hasMaxDate={true}
            disabled={myCase ? false : true}
          />
        </Col>
      </Row>
      {note?.recordingsExist === true && <ViewRecordings note={note} editNote={editNote} myCase={myCase} />}
      <Stack gap={3} className="mt-3">
        {fieldsWithOptions.map((field: any, index: number) => {
          let inputComponent;

          switch (field?.type) {
            case "checkbox":
              inputComponent = (
                <Col xs={3}>
                  <FormCheck
                    label={field?.label}
                    value={field?.value == "1" ? true : false}
                    onUpdate={(e: any) => updateField(e, field, index)}
                    css={"text-capitalize mb-1"}
                  />
                </Col>
              );
              break;

            case "date":
              inputComponent = (
                <Col xs={3}>
                  <div className="form-floating">
                    <label className="date-label text-capitalize">{field?.label} </label>
                    <input
                      id="floatingInput"
                      className={"form-control date-picker"}
                      type="date"
                      value={field?.value}
                      onChange={(e: any) => updateField(e, field, index)}
                      min={getMinDate()}
                      max={getMaxDate()}
                      pattern="\d{4}-\d{2}-\d{2}"
                    />
                  </div>
                </Col>
              );
              break;

            case "text":
              inputComponent = (
                <Col xs={12}>
                  <Input
                    label={field?.label}
                    type="text"
                    value={field?.value}
                    labelAbove={true}
                    css={"form-floating-label-wrap"}
                    onUpdate={(e: any) => updateField(e, field, index)}
                  />
                </Col>
              );
              break;

            case "multi-line text":
              inputComponent = (
                <Col xs={12}>
                  <Textarea
                    label={field?.label}
                    value={field?.value}
                    labelAbove={true}
                    onUpdate={(e: any) => updateField(e, field, index)}
                    css={"form-floating-label-wrap"}
                  />
                </Col>
              );
              break;

            case "dropdown":
              inputComponent = (
                <Col xs={6}>
                  <div className="form-floating">
                    <select
                      className="form-select"
                      id="floatingSelect"
                      aria-label="Floating label select example"
                      value={field?.value ?? ""}
                      onChange={(e: any) => updateField(e, field, index)}
                    >
                      <option value="" disabled>
                        Select from List
                      </option>
                      {field?.options &&
                        field.options.map((option: any, idx: number) => (
                          <option key={idx} value={option.name}>
                            {option.name}
                          </option>
                        ))}
                    </select>
                    <label className="text-capitalize">{field?.label}</label>
                  </div>
                </Col>
              );
              break;

            case "time":
              inputComponent = (
                <>
                  <Col xs={3}>
                    <SelectObject
                      label={field?.label + " Hours"}
                      value={(() => {
                        const total = Number(field?.value) || 0;
                        return String(Math.floor(total / 60));
                      })()}
                      options={possibleHours}
                      onUpdate={(e: any) => updateField(e, field, index, "hours")}
                      skipInitialOption={true}
                    />
                  </Col>
                  <Col xs={3}>
                    <SelectObject
                      label={field?.label + " Minutes"}
                      value={(() => {
                        const total = Number(field?.value) || 0;
                        return String(total % 60);
                      })()}
                      options={possibleMins}
                      onUpdate={(e: any) => updateField(e, field, index, "minutes")}
                      skipInitialOption={true}
                    />
                  </Col>
                </>
              );
              break;

            default:
              inputComponent = null;
          }

          return <Row>{inputComponent}</Row>;
        })}
      </Stack>
    </>
  );
};

export default CustomNote;
